import feFunctions from '../../services/fe-functions';

export default class AuthService {
  constructor(firebase, QueryParamService, EventService, PROVIDERS, $state, FlashService) {
    'ngInject';
    Object.assign( this, { firebase, PROVIDERS, EventService, $state, FlashService });
    this._profiles = {};
    this.qps = QueryParamService;
  }

  user() {
    return new Promise((res, rej) => {
      this.firebase.auth().onAuthStateChanged(user => {
        user ? res(user) : rej("Not Logged In!");
      });
    });
  }

  async provider() {
    try {
      const idToken = await this.idToken();
      return idToken ? idToken.claims.provider : null;
    } catch (error) {
      return null;
    }
  }

  async realm() {
    try {
      const idToken = await this.idToken();
      return idToken ? idToken.claims.realm : null;
    } catch (error) {
      // Not logged in
      return null;
    }
  }
  
  suppliedConnection() {
    const connString = this.qps.getRawParam('connection');
    if (!connString) return null;
    try {
      const decoded = decodeURIComponent(connString);
      return JSON.parse(decoded);
    } catch (error) {
      console.error("Invalid supplied connection parameter!");
      return null;
    }
  }

  includeConnectionInRequest(request) {
    const conn = this.suppliedConnection();
    return conn ? { ...request, connection: conn } : request;
  }

  async getProviderSettings (template) {
    const id = template? template.provider : await this.provider();
    if (id) return this.PROVIDERS[id];
    await this.logout(true);
    this.EventService.dispatchEvent(this.EventService.actions.AUTHENTICATE, {error: null});
    throw new Error("Unauthenticated!");
  }
  
  async idToken() {
    try {
      const user = await this.user();
      return user ? user.getIdTokenResult() : null;
    } catch (error) {
      return null;
    }
  }

  async isExpiredSession() {
    return new Promise((res, rej) => {
      this.firebase.auth().onAuthStateChanged(async user => {
        if (user) {
          const jwt = await user.getIdTokenResult();
          const { provider, connectionString } = jwt.claims;
          res(!provider || !connectionString);
        }
      });
    });
  }

  getProfile() {
    return this.firebase.auth().currentUser;
  }

  async updateProfile(profile, user) {
    this._profiles[user.uid] = profile;
    const { email } = profile;
    await user.updateProfile(profile);
    await user.updateEmail(email);
  }

  async login(authData) {
    const { realm, provider } = authData;
    const request = { authData, providerLocation: { realm, provider } };
    return this.signInWith(request, () => request);
  }

  // DB page magic link: the DB page already authenticated with the provider and
  // handed over a one-time code. The server creates the Firebase user before
  // minting the token, so there is no isNewUser second sign-in (and the code is
  // already deleted, so it could not be redeemed again anyway).
  async loginWithHandoff(handoffCode) {
    return this.signInWith({ handoffCode }, null);
  }

  // Function redirect after a magic-link login: one-time code that the function
  // swaps for this session's connection (the connection never goes in the URL).
  async issueFunctionAccessCode(target) {
    const authFE = feFunctions(this.firebase).httpsCallable('auth');
    const { "data": { accessCode } } = await authFE({ issueAccessCode: true, target });
    return accessCode;
  }

  async signInWith(request, retryRequest) {

    const authFE = feFunctions(this.firebase).httpsCallable('auth');
    // Drop the previous RTDB session while this ID token is still attached
    // (same or different uid). After signInWithCustomToken, logout would only
    // remove the new sessionId. auth() already minted the new row (sessionId is
    // in the JWT); if sign-in or this revoke fails, that new UUID is unused until
    // the next login's idle prune (30 days).
    const replacingExisting = !!this.firebase.auth().currentUser;
    const {
      "data": { firebaseToken, profile, sessionId }
    } = await authFE(request);

    if (typeof firebaseToken !== "string" || !firebaseToken.trim()) {
      throw new Error("Login failed.");
    }

    if (replacingExisting) {
      await this.revokeServerSession();
    }

    const response = await this.signInWithCustomToken(firebaseToken);
    const { user, additionalUserInfo } = response;
    await this.updateProfile(profile, user);

    if (additionalUserInfo?.isNewUser && retryRequest) { //run this whenever claims are modified
      // First ID token often lacks custom claims (sessionId). Drop session A
      // explicitly so the retry does not leave an orphan live row.
      await this.revokeServerSession(sessionId);
      await this.firebase.auth().signOut();
      const { "data": { firebaseToken: retryToken } } = await authFE(retryRequest(sessionId));
      if (typeof retryToken !== "string" || !retryToken.trim()) {
        throw new Error("Login failed.");
      }
      await this.signInWithCustomToken(retryToken);
    }

    return response;
  }

  async signInWithCustomToken(firebaseToken) {
    return await this.firebase.auth().signInWithCustomToken(firebaseToken);
  }
  
  async revokeServerSession(sessionId) {
    const authFE = feFunctions(this.firebase).httpsCallable('auth');
    const payload = sessionId
      ? { logout: true, sessionId }
      : { logout: true };
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await authFE(payload);
        return;
      } catch (err) {
        lastErr = err;
        if (attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
      }
    }
    throw lastErr;
  }

  async logout(noRedirect, error) {
    const providerId = await this.provider();
    // Do not firebase.auth().signOut() if auth({ logout: true }) failed.
    // Logout needs this ID token (context.auth) to drop the RTDB row. Signing
    // out locally first leaves a live session until the idle prune (30 days),
    // with no way to retry. Flash "try again"; keep tokens; caller retries.
    if (this.firebase.auth().currentUser) {
      try {
        await this.revokeServerSession();
      } catch (err) {
        console.error("Server logout failed", err);
        this.FlashService.error("Could not log out. Please try again.");
        throw err;
      }
    }
    await this.firebase.auth().signOut();
    // $state.go is preventing firebase.auth().onAuthStateChanged from firing
    return noRedirect ? true : this.$state.go('login', { provider: providerId, validationErrorMessage: error?.details?.validationErrorMessage }, {});
  }
}
